/**
 * KbContenutoFormDialog — l'editor dei contenuti di piattaforma (`kb_contents`).
 *
 * Scrive SEMPRE una BOZZA, e solo una bozza: `stato: 'pubblicato'` non si
 * raggiunge da qui, si raggiunge da `PubblicaDialog` → `pubblicaKbContenuto`.
 * Questo significa che non esiste il caso «ho salvato mezza modifica e i
 * commerciali la stanno leggendo»: il lettore vede sempre e solo la revisione
 * pubblicata.
 *
 * TRE SCELTE CHE NON SONO ESTETICA
 *  1. Gli errori di `validaContenuto` compaiono ACCANTO AL CAMPO, distinguendo
 *     «non posso salvare» da «non potrai pubblicare»: un elenco in un alert
 *     generico costringe a cercare il campo a mano ed è il motivo per cui la
 *     gente pubblica contenuti incompleti.
 *  2. Le AREE sono un vocabolario chiuso (`kb_areas`), non testo libero:
 *     `noleggio-operativo` e `noleggio_operativo` non devono poter convivere,
 *     altrimenti la regola anti-obsoleti cerca la prima e trova la seconda —
 *     cioè niente, in silenzio.
 *  3. Le due capability hanno etichette che dicono cosa fanno DAVVERO: la prima
 *     non nasconde nulla, la seconda sì. Confonderle vuol dire nascondere il
 *     manuale del gestionale a un cliente che l'ha pagato.
 *
 * Il codice non sa cosa sia un noleggio operativo, né un fotovoltaico: legge
 * aree, capability e livelli dai DATI (`kb_areas`, `tenants`,
 * `kb_settings/governance`).
 *
 * Doc canonico: docs/01-ARCHITECTURE/KB-CONTENT-MODEL.md
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, AlertTitle, Autocomplete, Box, Button, Chip, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel,
  LinearProgress, MenuItem, Paper, Stack, Switch, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  ImageOutlined, Save as SaveIcon, VisibilityOutlined, VisibilityOffOutlined,
} from '@mui/icons-material';
import { tokens } from '../../theme';
import { SoftChip } from '../SoftChip';
import { auth } from '../../firebase';
import { AnteprimaDestinatari } from './AnteprimaDestinatari';
import {
  KB_FRESCHEZZA_HINT,
  KB_HINT_AREE_COPERTE,
  KB_HINT_CAPABILITY_INFORMATIVA,
  KB_HINT_CAPABILITY_RICHIESTA,
  KB_HINT_INDIRIZZO,
  KB_HINT_ROTTA_AZIONE,
  aValoreInputData,
  daValoreInputData,
  etichettaDaId,
} from './labels';
import {
  KB_FORMATI_EDITOR,
  caricaImmagineCorpo,
  capabilityDisponibili,
  ruoliDisponibili,
  salvaBozzaKbContenuto,
} from '../../services/kbContentsService';
import {
  KB_FORMATO_LABEL,
  KB_FRESCHEZZE,
  KB_FRESCHEZZA_LABEL,
  KB_LIVELLI_IMPATTO,
  KB_LIVELLO_LABEL,
  KB_RIASSUNTO_MAX,
  slugDaTitolo,
  validaContenuto,
  type KbErroreValidazione,
} from '@shared/kb/kbContentModel';
import { buildAudienceKeys } from '@shared/kb/kbAudience';
import { calcolaScadenzaEvidenza } from '@shared/kb/kbStatoLettura';
import { aMillis } from '@shared/kb/kbTypes';
import type {
  KbArea,
  KbCanale,
  KbContenuto,
  KbFormato,
  KbFreschezza,
  KbLivelloImpatto,
  KbModalitaTenant,
} from '@shared/kb/kbTypes';
import type {
  KbCorpoCaricato,
  KbGovernanceCaricata,
  KbPlatea,
  SalvaBozzaKbResponse,
} from '../../types/kb';

interface Props {
  open: boolean;
  /** Contenuto da modificare; `null` = nuovo. */
  contenuto: KbContenuto | null;
  /** Corpo già caricato (bozza se c'è, altrimenti pubblicata); `null` se nuovo. */
  corpoIniziale: KbCorpoCaricato | null;
  /** Vocabolario delle aree funzionali (`kb_areas`, solo le attive). */
  aree: KbArea[];
  /** Tutte le testate: alimenta i correlati e il controllo di indirizzo occupato. */
  contenutiEsistenti: KbContenuto[];
  platea: KbPlatea | null;
  errorePlatea: string | null;
  governance: KbGovernanceCaricata;
  onClose: () => void;
  onSalvato: (esito: SalvaBozzaKbResponse) => void;
}

/** Bersagli touch: la console si usa anche da tablet. */
const TOUCH_MIN = 40;

/** Lingua dei contenuti di piattaforma in fase 1 (il multilingua è fase successiva). */
const LINGUA_DEFAULT = 'it';

/** Tetto delle immagini del corpo, allineato a `storage.rules` su `kb_media/**`. */
const MAX_BYTE_IMMAGINE = 10 * 1024 * 1024;

const MODALITA_TENANT_LABEL: Record<KbModalitaTenant, string> = {
  tutti: 'tutte le aziende',
  inclusi: 'solo le aziende che indico',
  esclusi: 'tutte tranne le aziende che indico',
};

// ============================================================================
// ANTEPRIMA MARKDOWN — indicativa, dichiaratamente approssimativa
// ============================================================================

/**
 * Anteprima del corpo. Copre titoli, elenchi, grassetto/corsivo, codice,
 * immagini e link: quanto basta per accorgersi di una struttura sbagliata.
 *
 * ⚠️ NON è il renderer del gestionale (che vive nell'app e usa la sua
 * libreria): la resa finale può differire, e la UI lo scrive. Meglio
 * un'anteprima dichiarata approssimativa che nessuna anteprima — ma non si
 * spaccia per definitiva.
 *
 * Il testo viene ESCAPATO prima di qualunque trasformazione: nel corpo può
 * finire HTML incollato per sbaglio e non deve essere interpretato.
 */
function anteprimaHtml(markdown: string): string {
  // Le virgolette rientrano nell'escape: un URL o un testo alternativo che ne
  // contiene una uscirebbe dall'attributo HTML in cui viene inserito.
  const esc = (s: string) =>
    s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

  // Solo schemi innocui: un `javascript:` in un corpo markdown non deve
  // diventare un link cliccabile nemmeno in un'anteprima interna.
  const urlSicuro = (url: string): string | null => {
    const u = url.trim();
    if (/^(https?:|mailto:)/i.test(u)) return u;
    if (u.startsWith('/') || u.startsWith('#') || u.startsWith('./')) return u;
    return null;
  };

  const inline = (s: string) =>
    esc(s)
      .replace(/!\[([^\]]*)\]\(([^)\s]+)\)/g, (_m, alt: string, url: string) => {
        const sicuro = urlSicuro(url);
        return sicuro ? `<img alt="${alt}" src="${sicuro}" />` : `[immagine con indirizzo non valido]`;
      })
      .replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (_m, testo: string, url: string) => {
        const sicuro = urlSicuro(url);
        return sicuro
          ? `<a href="${sicuro}" target="_blank" rel="noopener noreferrer">${testo}</a>`
          : testo;
      })
      .replace(/`([^`]+)`/g, '<code>$1</code>')
      .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
      .replace(/(^|[^*])\*([^*]+)\*/g, '$1<em>$2</em>');

  const out: string[] = [];
  let inCodice = false;
  let tipoElenco: 'ul' | 'ol' | null = null;

  const chiudiElenco = () => {
    if (tipoElenco) {
      out.push(`</${tipoElenco}>`);
      tipoElenco = null;
    }
  };

  for (const riga of markdown.replace(/\r\n/g, '\n').split('\n')) {
    if (/^```/.test(riga.trim())) {
      chiudiElenco();
      out.push(inCodice ? '</code></pre>' : '<pre><code>');
      inCodice = !inCodice;
      continue;
    }
    if (inCodice) {
      out.push(`${esc(riga)}\n`);
      continue;
    }
    const titolo = riga.match(/^(#{1,6})\s+(.*)$/);
    if (titolo) {
      chiudiElenco();
      const livello = Math.min(6, titolo[1].length);
      out.push(`<h${livello}>${inline(titolo[2])}</h${livello}>`);
      continue;
    }
    const voceUl = riga.match(/^\s*[-*]\s+(.*)$/);
    if (voceUl) {
      if (tipoElenco !== 'ul') {
        chiudiElenco();
        out.push('<ul>');
        tipoElenco = 'ul';
      }
      out.push(`<li>${inline(voceUl[1])}</li>`);
      continue;
    }
    const voceOl = riga.match(/^\s*\d+[.)]\s+(.*)$/);
    if (voceOl) {
      if (tipoElenco !== 'ol') {
        chiudiElenco();
        out.push('<ol>');
        tipoElenco = 'ol';
      }
      out.push(`<li>${inline(voceOl[1])}</li>`);
      continue;
    }
    if (!riga.trim()) {
      chiudiElenco();
      continue;
    }
    chiudiElenco();
    out.push(`<p>${inline(riga)}</p>`);
  }
  chiudiElenco();
  if (inCodice) out.push('</code></pre>');
  return out.join('');
}

// ============================================================================
// HELPER DI PRESENTAZIONE
// ============================================================================

/** Errori raggruppati per campo: la UI li mostra dove sta il problema. */
function indicizzaErrori(errori: readonly KbErroreValidazione[]): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const e of errori) {
    (out[e.campo] ??= []).push(e.messaggio);
  }
  return out;
}

const Sezione: React.FC<{
  titolo: string;
  descrizione?: string;
  children: React.ReactNode;
}> = ({ titolo, descrizione, children }) => (
  <Box>
    <Typography
      sx={{
        fontFamily: tokens.font.display,
        fontWeight: 600,
        fontSize: '1.02rem',
        color: tokens.color.graphite,
      }}
    >
      {titolo}
    </Typography>
    {descrizione && (
      <Typography variant="caption" sx={{ color: tokens.color.mist, display: 'block', mb: 1.25 }}>
        {descrizione}
      </Typography>
    )}
    <Stack spacing={2} sx={{ mt: descrizione ? 0 : 1.25 }}>{children}</Stack>
  </Box>
);

// ============================================================================
// COMPONENTE
// ============================================================================

export const KbContenutoFormDialog: React.FC<Props> = ({
  open, contenuto, corpoIniziale, aree, contenutiEsistenti, platea, errorePlatea,
  governance, onClose, onSalvato,
}) => {
  const inModifica = contenuto !== null;

  // ── identità e testo ──
  const [formato, setFormato] = useState<KbFormato>('novita');
  const [titolo, setTitolo] = useState('');
  const [slug, setSlug] = useState('');
  const [slugToccato, setSlugToccato] = useState(false);
  const [riassunto, setRiassunto] = useState('');
  const [corpo, setCorpo] = useState('');
  const [anteprimaAperta, setAnteprimaAperta] = useState(false);

  // ── classificazione e responsabilità ──
  const [areeSel, setAreeSel] = useState<string[]>([]);
  const [ownerUid, setOwnerUid] = useState('');
  const [freschezza, setFreschezza] = useState<KbFreschezza>('attuale');
  const [revisioneGiorni, setRevisioneGiorni] = useState('');

  // ── visibilità ──
  const [tuttiRuoli, setTuttiRuoli] = useState(true);
  const [ruoliSel, setRuoliSel] = useState<string[]>([]);
  const [modalitaTenant, setModalitaTenant] = useState<KbModalitaTenant>('tutti');
  const [tenantSel, setTenantSel] = useState<string[]>([]);
  const [capInformativa, setCapInformativa] = useState('');
  const [capRichiesta, setCapRichiesta] = useState('');

  // ── annuncio (solo formato novità) ──
  const [livello, setLivello] = useState<KbLivelloImpatto>('rilevante');
  const [canali, setCanali] = useState<KbCanale[]>(['in_app']);
  const [dataAnnuncio, setDataAnnuncio] = useState<number | null>(null);
  const [scadenza, setScadenza] = useState<number | null>(null);
  const [scadenzaToccata, setScadenzaToccata] = useState(false);
  const [rottaApp, setRottaApp] = useState('');
  const [etichettaAzione, setEtichettaAzione] = useState('');
  const [releaseRef, setReleaseRef] = useState('');
  const [correlati, setCorrelati] = useState<string[]>([]);
  /** Campi dell'annuncio che l'editor non mostra ma non deve perdere. */
  const [iniziativaId, setIniziativaId] = useState<string | null>(null);

  // ── salvataggio e immagini ──
  const [salvataggio, setSalvataggio] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  const [progressoImmagine, setProgressoImmagine] = useState<number | null>(null);
  const inputImmagineRef = useRef<HTMLInputElement | null>(null);
  const corpoRef = useRef<HTMLTextAreaElement | null>(null);

  /**
   * Chiave dell'APERTURA corrente.
   *
   * L'inizializzazione dei campi deve avvenire una volta per apertura, non a
   * ogni cambio delle prop: `governance` e `platea` arrivano da letture
   * asincrone della pagina e possono aggiornarsi mentre il dialogo è aperto.
   * Senza questo controllo, il testo appena scritto verrebbe azzerato quando
   * finisce di caricare la platea — un bug che si manifesta solo su rete lenta,
   * cioè quando fa più male.
   */
  const chiaveApertura = open ? (contenuto?.id ?? '__nuovo__') : null;
  const ultimaApertura = useRef<string | null>(null);

  // ── inizializzazione all'apertura ──
  useEffect(() => {
    if (chiaveApertura === null) {
      ultimaApertura.current = null;
      return;
    }
    if (ultimaApertura.current === chiaveApertura) return;
    ultimaApertura.current = chiaveApertura;

    setErrore(null);
    setSalvataggio(false);
    setProgressoImmagine(null);
    setAnteprimaAperta(false);

    if (contenuto) {
      setFormato(contenuto.formato);
      setTitolo(contenuto.titolo ?? '');
      setSlug(contenuto.id);
      setSlugToccato(true);
      setRiassunto(contenuto.riassunto ?? '');
      setCorpo(corpoIniziale?.corpo_markdown ?? '');
      setAreeSel(contenuto.aree_coperte ?? []);
      setOwnerUid(contenuto.owner_uid ?? '');
      setFreschezza(contenuto.stato_freschezza ?? 'attuale');
      setRevisioneGiorni(
        typeof contenuto.revisione_ogni_giorni === 'number'
          ? String(contenuto.revisione_ogni_giorni)
          : '',
      );
      setTuttiRuoli(contenuto.audience?.tutti_i_ruoli ?? true);
      setRuoliSel(contenuto.audience?.ruoli ?? []);
      setModalitaTenant(contenuto.visibilita_tenant?.modalita ?? 'tutti');
      setTenantSel(contenuto.visibilita_tenant?.tenant_ids ?? []);
      setCapInformativa(contenuto.visibilita_tenant?.capability_informativa ?? '');
      setCapRichiesta(contenuto.visibilita_tenant?.capability_richiesta ?? '');

      const a = contenuto.annuncio;
      setLivello(a?.livello_impatto ?? 'rilevante');
      setCanali(a?.canali?.length ? a.canali : ['in_app']);
      setDataAnnuncio(aMillis(a?.data_annuncio));
      setScadenza(aMillis(a?.scadenza_evidenza_at));
      setScadenzaToccata(true);
      setRottaApp(a?.rotta_app ?? '');
      setEtichettaAzione(a?.etichetta_azione ?? '');
      setReleaseRef(a?.release_ref ?? '');
      setCorrelati(a?.contenuti_correlati ?? []);
      setIniziativaId(a?.iniziativa_id ?? null);
      return;
    }

    // ── nuovo contenuto ──
    const oggi = new Date();
    oggi.setHours(0, 0, 0, 0);
    setFormato('novita');
    setTitolo('');
    setSlug('');
    setSlugToccato(false);
    setRiassunto('');
    setCorpo('');
    setAreeSel([]);
    setOwnerUid(auth.currentUser?.uid ?? '');
    setFreschezza('attuale');
    setRevisioneGiorni(String(governance.governance.giorni_validita_default));
    setTuttiRuoli(true);
    setRuoliSel([]);
    setModalitaTenant('tutti');
    setTenantSel([]);
    setCapInformativa('');
    setCapRichiesta('');
    setLivello('rilevante');
    setCanali(governance.governance.livelli_impatto.rilevante?.canali_default ?? ['in_app']);
    setDataAnnuncio(oggi.getTime());
    setScadenza(
      calcolaScadenzaEvidenza(
        oggi.getTime(),
        governance.governance.livelli_impatto.rilevante?.giorni_evidenza ?? 30,
      ),
    );
    setScadenzaToccata(false);
    setRottaApp('');
    setEtichettaAzione('');
    setReleaseRef('');
    setCorrelati([]);
    setIniziativaId(null);
  }, [chiaveApertura, contenuto, corpoIniziale, governance]);

  // ── indirizzo proposto dal titolo finché l'editore non lo tocca ──
  // La forma dell'indirizzo NON si ri-valida qui con `KB_SLUG_REGEX`:
  // `validaContenuto` lo fa già sul campo `id` e il suo messaggio compare sotto
  // il campo. Due controlli della stessa regola sono due regole che possono
  // divergere.
  const slugEffettivo = useMemo(
    () => (slugToccato ? slug.trim() : slugDaTitolo(titolo)),
    [slug, slugToccato, titolo],
  );

  const indirizzoOccupato = useMemo(() => {
    if (inModifica || !slugEffettivo) return false;
    return contenutiEsistenti.some((c) => c.id === slugEffettivo);
  }, [inModifica, slugEffettivo, contenutiEsistenti]);

  /** Comportamento del livello scelto, letto dai dati (non da un `if`). */
  const configLivello = governance.governance.livelli_impatto[livello];

  const cambiaLivello = (nuovo: KbLivelloImpatto) => {
    setLivello(nuovo);
    const conf = governance.governance.livelli_impatto[nuovo];
    setCanali(conf?.canali_default?.length ? [...conf.canali_default] : ['in_app']);
    if (!scadenzaToccata) {
      setScadenza(calcolaScadenzaEvidenza(dataAnnuncio, conf?.giorni_evidenza ?? 30));
    }
  };

  const cambiaDataAnnuncio = (valore: string) => {
    const ms = daValoreInputData(valore);
    setDataAnnuncio(ms);
    if (!scadenzaToccata) {
      setScadenza(calcolaScadenzaEvidenza(ms, configLivello?.giorni_evidenza ?? 30));
    }
  };

  // ── il contenuto come sarà: una sola composizione, usata da tutti ──
  const giorniRevisione = useMemo(() => {
    const n = Number(revisioneGiorni);
    return Number.isFinite(n) && n > 0 ? Math.round(n) : null;
  }, [revisioneGiorni]);

  const contenutoComposto = useMemo<KbContenuto>(() => {
    const annuncio =
      formato === 'novita'
        ? {
            livello_impatto: livello,
            canali,
            data_annuncio: dataAnnuncio,
            scadenza_evidenza_at: scadenza,
            contenuti_correlati: correlati,
            rotta_app: rottaApp.trim() || null,
            etichetta_azione: etichettaAzione.trim() || null,
            iniziativa_id: iniziativaId,
            release_ref: releaseRef.trim() || null,
          }
        : null;

    return {
      id: slugEffettivo,
      scope: 'piattaforma',
      formato,
      revisione_pubblicata: contenuto?.revisione_pubblicata ?? null,
      revisione_bozza: contenuto?.revisione_bozza ?? null,
      titolo: titolo.trim(),
      riassunto: riassunto.trim(),
      lingua: contenuto?.lingua ?? LINGUA_DEFAULT,
      taxonomy_path: contenuto?.taxonomy_path ?? [],
      tags: contenuto?.tags ?? [],
      aree_coperte: areeSel,
      audience: { tutti_i_ruoli: tuttiRuoli, ruoli: ruoliSel },
      visibilita_tenant: {
        modalita: modalitaTenant,
        tenant_ids: modalitaTenant === 'tutti' ? [] : tenantSel,
        capability_richiesta: capRichiesta.trim() || null,
        capability_informativa: capInformativa.trim() || null,
      },
      stato: contenuto?.stato ?? 'bozza',
      stato_freschezza: freschezza,
      revisione_ogni_giorni: giorniRevisione,
      owner_uid: ownerUid,
      versione_maggiore: contenuto?.versione_maggiore ?? 1,
      versione_minore: contenuto?.versione_minore ?? 0,
      annuncio,
    };
  }, [
    formato, livello, canali, dataAnnuncio, scadenza, correlati, rottaApp, etichettaAzione,
    iniziativaId, releaseRef, slugEffettivo, contenuto, titolo, riassunto, areeSel, tuttiRuoli,
    ruoliSel, modalitaTenant, tenantSel, capRichiesta, capInformativa, freschezza,
    giorniRevisione, ownerUid,
  ]);

  // ── validazione: cosa blocca ADESSO e cosa bloccherà la pubblicazione ──
  const erroriOra = useMemo(
    () =>
      indicizzaErrori(
        validaContenuto(contenutoComposto, { corpoMarkdown: corpo, perPubblicazione: false })
          .errori,
      ),
    [contenutoComposto, corpo],
  );

  const erroriPubblicazione = useMemo(() => {
    const esito = validaContenuto(contenutoComposto, {
      corpoMarkdown: corpo,
      perPubblicazione: true,
    });
    // `motivo_ultima_modifica` si scrive nel dialogo di pubblicazione, non qui:
    // segnalarlo in questa schermata manderebbe l'editore a cercare un campo
    // che in questa schermata non esiste.
    return indicizzaErrori(esito.errori.filter((e) => e.campo !== 'motivo_ultima_modifica'));
  }, [contenutoComposto, corpo]);

  const bloccanti = Object.keys(erroriOra).length > 0 || indirizzoOccupato;
  const mancanzePubblicazione = Object.entries(erroriPubblicazione);

  const helper = (campo: string, base?: string): string | undefined => {
    const ora = erroriOra[campo];
    if (ora?.length) return ora.join(' · ');
    const dopo = erroriPubblicazione[campo];
    if (dopo?.length) return `Per pubblicare: ${dopo.join(' · ')}`;
    return base;
  };

  const inErrore = (campo: string) => Boolean(erroriOra[campo]?.length);

  // ── opzioni dai dati ──
  const opzioniRuoli = useMemo(
    () => ruoliDisponibili(platea?.utenti ?? [], ruoliSel),
    [platea, ruoliSel],
  );

  const opzioniCapability = useMemo(
    () =>
      capabilityDisponibili(platea?.tenants ?? [], [capInformativa, capRichiesta].filter(Boolean)),
    [platea, capInformativa, capRichiesta],
  );

  const opzioniCorrelati = useMemo(
    () => contenutiEsistenti.filter((c) => c.id !== slugEffettivo).map((c) => c.id),
    [contenutiEsistenti, slugEffettivo],
  );

  const chiaviAudience = useMemo(
    () => buildAudienceKeys({ tutti_i_ruoli: tuttiRuoli, ruoli: ruoliSel }),
    [tuttiRuoli, ruoliSel],
  );

  // ── immagini nel corpo ──
  const handleImmagine = async (file: File | null) => {
    if (!file || !contenuto) return;
    // Gli stessi due limiti che impone `storage.rules` su `kb_media/**`
    // (immagini, sotto i 10 MB): controllarli qui trasforma un errore di
    // permesso incomprensibile in una frase che dice cosa fare.
    if (!file.type.startsWith('image/')) {
      setErrore('Nel corpo si possono inserire solo immagini.');
      return;
    }
    if (file.size >= MAX_BYTE_IMMAGINE) {
      setErrore('L\'immagine supera i 10 MB: una schermata del gestionale sta in molto meno.');
      return;
    }
    setErrore(null);
    setProgressoImmagine(0);
    try {
      const caricata = await caricaImmagineCorpo(contenuto.id, file, setProgressoImmagine);
      // Inserimento nel punto del cursore: reincollare a mano un URL lungo è il
      // modo più semplice di sbagliarlo.
      const area = corpoRef.current;
      const posizione = area?.selectionStart ?? corpo.length;
      setCorpo(
        `${corpo.slice(0, posizione)}\n\n${caricata.markdown}\n\n${corpo.slice(posizione)}`,
      );
    } catch (e) {
      setErrore(e instanceof Error ? e.message : 'Errore durante il caricamento dell\'immagine');
    } finally {
      setProgressoImmagine(null);
      if (inputImmagineRef.current) inputImmagineRef.current.value = '';
    }
  };

  // ── salvataggio ──
  const handleSalva = async () => {
    if (bloccanti) {
      setErrore('Alcuni campi non sono compilabili così: controlla i messaggi in rosso.');
      return;
    }
    setSalvataggio(true);
    setErrore(null);
    try {
      const esito = await salvaBozzaKbContenuto({
        content_id: slugEffettivo || undefined,
        // Solo i campi EDITABILI: `audience_keys`, `ordinamento_at`,
        // `content_hash`, `taxonomy_root_id/leaf_id` e `revisione_scadenza_at`
        // sono DERIVATI e hanno un unico proprietario, il trigger. Mandarli da
        // qui creerebbe una seconda fonte per lo stesso valore.
        contenuto: {
          scope: contenutoComposto.scope,
          formato: contenutoComposto.formato,
          titolo: contenutoComposto.titolo,
          riassunto: contenutoComposto.riassunto,
          lingua: contenutoComposto.lingua,
          taxonomy_path: contenutoComposto.taxonomy_path,
          tags: contenutoComposto.tags,
          aree_coperte: contenutoComposto.aree_coperte,
          audience: contenutoComposto.audience,
          visibilita_tenant: contenutoComposto.visibilita_tenant,
          stato_freschezza: contenutoComposto.stato_freschezza,
          revisione_ogni_giorni: contenutoComposto.revisione_ogni_giorni,
          // Senza responsabile il campo si OMETTE, non si manda vuoto: la
          // callable rifiuta un `owner_uid` vuoto e la bozza non si salverebbe
          // affatto. Un contenuto senza responsabile è salvabile e non
          // pubblicabile (lo dice `validaContenuto`) e compare nella lista di
          // manutenzione: è il comportamento giusto, non un errore da schermo.
          owner_uid: contenutoComposto.owner_uid || undefined,
          annuncio: contenutoComposto.annuncio,
        },
        corpo_markdown: corpo,
      });
      onSalvato(esito);
      onClose();
    } catch (e) {
      setErrore(e instanceof Error ? e.message : 'Errore durante il salvataggio');
    } finally {
      setSalvataggio(false);
    }
  };

  const utentiOwner = platea?.utenti ?? [];
  const ownerScelto = utentiOwner.find((u) => u.uid === ownerUid) ?? null;

  return (
    <Dialog open={open} onClose={salvataggio ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontFamily: tokens.font.display, fontWeight: 600 }}>
        {inModifica ? `Modifica «${contenuto?.titolo}»` : 'Nuovo contenuto di piattaforma'}
      </DialogTitle>

      <DialogContent dividers>
        {errore && <Alert severity="error" sx={{ mb: 2 }}>{errore}</Alert>}

        {aree.length === 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Il vocabolario delle aree funzionali (<code>kb_areas</code>) è vuoto: senza almeno
            un&apos;area il contenuto si salva come bozza ma non si può pubblicare. Va popolato
            dallo script di seed.
          </Alert>
        )}

        {mancanzePubblicazione.length > 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <AlertTitle sx={{ fontWeight: 700 }}>Si salva, ma non si pubblica ancora</AlertTitle>
            <Stack component="ul" spacing={0.25} sx={{ pl: 2.5, my: 0 }}>
              {mancanzePubblicazione.map(([campo, messaggi]) => (
                <Typography component="li" variant="body2" key={campo}>
                  {messaggi.join(' · ')}
                </Typography>
              ))}
            </Stack>
          </Alert>
        )}

        <Stack spacing={3}>
          {/* ================= COS'È ================= */}
          <Sezione
            titolo="Che contenuto è"
            descrizione="Una novità compare in home e avvisa; un articolo vive nella guida e si cerca."
          >
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                select
                label="Formato"
                value={formato}
                disabled={salvataggio}
                onChange={(e) => setFormato(e.target.value as KbFormato)}
                sx={{ minWidth: 200 }}
                helperText={helper('formato', 'il video si gestisce nella sua tab')}
                error={inErrore('formato')}
              >
                {KB_FORMATI_EDITOR.map((f) => (
                  <MenuItem key={f} value={f} sx={{ minHeight: TOUCH_MIN }}>
                    {KB_FORMATO_LABEL[f]}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Titolo"
                required
                value={titolo}
                disabled={salvataggio}
                onChange={(e) => setTitolo(e.target.value)}
                fullWidth
                error={inErrore('titolo')}
                helperText={helper('titolo')}
              />
            </Stack>

            <TextField
              label="Indirizzo"
              value={slugEffettivo}
              disabled={salvataggio || inModifica}
              onChange={(e) => {
                setSlugToccato(true);
                setSlug(e.target.value);
              }}
              fullWidth
              error={inErrore('id') || indirizzoOccupato}
              helperText={
                indirizzoOccupato
                  ? 'Questo indirizzo è già usato da un altro contenuto: cambialo.'
                  : inModifica
                    ? 'L\'indirizzo non si cambia dopo la creazione.'
                    : helper('id', KB_HINT_INDIRIZZO)
              }
              InputProps={{
                sx: { fontFamily: tokens.font.mono, fontSize: '0.85rem' },
              }}
            />

            <TextField
              label="Riassunto"
              value={riassunto}
              disabled={salvataggio}
              onChange={(e) => setRiassunto(e.target.value)}
              multiline
              minRows={2}
              fullWidth
              error={inErrore('riassunto')}
              helperText={
                helper('riassunto') ??
                `${riassunto.length}/${KB_RIASSUNTO_MAX} — è ciò che si legge nel riquadro in home`
              }
            />
          </Sezione>

          <Divider />

          {/* ================= TESTO ================= */}
          <Sezione titolo="Testo">
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={anteprimaAperta ? <VisibilityOffOutlined /> : <VisibilityOutlined />}
                onClick={() => setAnteprimaAperta((v) => !v)}
                sx={{ minHeight: TOUCH_MIN, touchAction: 'manipulation' }}
              >
                {anteprimaAperta ? 'Torna al testo' : 'Anteprima'}
              </Button>
              <input
                ref={inputImmagineRef}
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => handleImmagine(e.target.files?.[0] ?? null)}
              />
              <Tooltip
                title={
                  inModifica
                    ? 'Carica un\'immagine e inseriscila nel punto del cursore'
                    : 'Salva prima la bozza: le immagini si archiviano sotto l\'indirizzo del contenuto'
                }
              >
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ImageOutlined />}
                    disabled={!inModifica || salvataggio || progressoImmagine !== null}
                    onClick={() => inputImmagineRef.current?.click()}
                    sx={{ minHeight: TOUCH_MIN, touchAction: 'manipulation' }}
                  >
                    Immagine
                  </Button>
                </span>
              </Tooltip>
              {erroriPubblicazione['corpo_markdown'] && (
                <SoftChip label="il testo è obbligatorio per pubblicare" tone="warning" />
              )}
            </Stack>

            {progressoImmagine !== null && (
              <Box>
                <LinearProgress
                  variant="determinate"
                  value={progressoImmagine}
                  sx={{ borderRadius: 2, height: 6 }}
                />
                <Typography variant="caption" sx={{ color: tokens.color.ash, fontFamily: tokens.font.mono }}>
                  Caricamento {Math.round(progressoImmagine)}%
                </Typography>
              </Box>
            )}

            {anteprimaAperta ? (
              <Paper
                variant="outlined"
                sx={{ p: 2, borderColor: tokens.color.line, bgcolor: tokens.color.paper }}
              >
                <Typography variant="caption" sx={{ color: tokens.color.mist, display: 'block', mb: 1 }}>
                  Anteprima indicativa: la resa nel gestionale può differire.
                </Typography>
                <Box
                  sx={{
                    '& img': { maxWidth: '100%', borderRadius: 1 },
                    '& pre': {
                      bgcolor: tokens.color.bone,
                      p: 1.25,
                      borderRadius: 1,
                      overflowX: 'auto',
                      fontFamily: tokens.font.mono,
                      fontSize: '0.8rem',
                    },
                    '& code': { fontFamily: tokens.font.mono, fontSize: '0.85em' },
                    '& h1, & h2, & h3, & h4, & h5, & h6': {
                      fontFamily: tokens.font.display,
                      fontWeight: 600,
                      marginBottom: '0.4em',
                    },
                    '& p': { lineHeight: 1.6 },
                  }}
                  dangerouslySetInnerHTML={{ __html: anteprimaHtml(corpo) }}
                />
              </Paper>
            ) : (
              <TextField
                label="Corpo del contenuto (markdown)"
                value={corpo}
                disabled={salvataggio}
                onChange={(e) => setCorpo(e.target.value)}
                multiline
                minRows={12}
                fullWidth
                inputRef={corpoRef}
                InputProps={{ sx: { fontFamily: tokens.font.mono, fontSize: '0.85rem' } }}
                helperText={helper('corpo_markdown')}
                error={inErrore('corpo_markdown')}
              />
            )}
          </Sezione>

          <Divider />

          {/* ================= CLASSIFICAZIONE ================= */}
          <Sezione titolo="A cosa si riferisce">
            <Autocomplete
              multiple
              options={aree}
              disabled={salvataggio}
              value={aree.filter((a) => areeSel.includes(a.id))}
              getOptionLabel={(a) => a.label ?? a.id}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              onChange={(_e, value) => setAreeSel(value.map((a) => a.id))}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const { key, ...tagProps } = getTagProps({ index });
                  return <Chip key={key} size="small" label={option.label ?? option.id} {...tagProps} />;
                })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Aree funzionali"
                  error={inErrore('aree_coperte')}
                  helperText={helper('aree_coperte', KB_HINT_AREE_COPERTE)}
                />
              )}
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Autocomplete
                options={utentiOwner}
                disabled={salvataggio}
                value={ownerScelto}
                getOptionLabel={(u) => u.etichetta}
                isOptionEqualToValue={(a, b) => a.uid === b.uid}
                onChange={(_e, value) => setOwnerUid(value?.uid ?? '')}
                sx={{ flex: 1 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Responsabile del contenuto"
                    error={inErrore('owner_uid')}
                    helperText={helper('owner_uid', 'senza responsabile nessuno lo aggiornerà')}
                  />
                )}
              />
              <TextField
                select
                label="Allineamento al gestionale"
                value={freschezza}
                disabled={salvataggio}
                onChange={(e) => setFreschezza(e.target.value as KbFreschezza)}
                helperText={KB_FRESCHEZZA_HINT[freschezza]}
                sx={{ minWidth: 230 }}
              >
                {KB_FRESCHEZZE.map((f) => (
                  <MenuItem key={f} value={f} sx={{ minHeight: TOUCH_MIN }}>
                    {KB_FRESCHEZZA_LABEL[f]}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Rivedere ogni (giorni)"
                value={revisioneGiorni}
                disabled={salvataggio}
                onChange={(e) => setRevisioneGiorni(e.target.value.replace(/[^\d]/g, ''))}
                inputProps={{ inputMode: 'numeric' }}
                helperText="vuoto = nessuna scadenza"
                sx={{ width: 190 }}
              />
            </Stack>
          </Sezione>

          <Divider />

          {/* ================= CHI LO VEDE ================= */}
          <Sezione
            titolo="Chi lo vede"
            descrizione="Il ruolo e il piano sono filtri di rilevanza, non un confine di sicurezza: chi conosce l'indirizzo di un contenuto di piattaforma lo legge comunque."
          >
            <FormControlLabel
              sx={{ minHeight: TOUCH_MIN, touchAction: 'manipulation' }}
              control={
                <Switch
                  checked={tuttiRuoli}
                  disabled={salvataggio}
                  onChange={(e) => setTuttiRuoli(e.target.checked)}
                />
              }
              label={
                <Typography variant="body2">
                  Tutti i ruoli
                  {!tuttiRuoli && (
                    <Box component="span" sx={{ color: tokens.color.mist }}>
                      {' '}— scegli sotto a chi è destinato
                    </Box>
                  )}
                </Typography>
              }
            />

            {!tuttiRuoli && (
              <Autocomplete
                multiple
                options={opzioniRuoli}
                disabled={salvataggio}
                value={ruoliSel}
                getOptionLabel={etichettaDaId}
                onChange={(_e, value) => setRuoliSel(value)}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const { key, ...tagProps } = getTagProps({ index });
                    return <Chip key={key} size="small" label={etichettaDaId(option)} {...tagProps} />;
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Ruoli destinatari"
                    error={inErrore('audience')}
                    helperText={helper(
                      'audience',
                      'elenco costruito dai ruoli realmente presenti fra gli utenti',
                    )}
                  />
                )}
              />
            )}

            <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', rowGap: 0.75 }}>
              <Typography variant="caption" sx={{ color: tokens.color.mist, alignSelf: 'center' }}>
                chiavi che verranno scritte:
              </Typography>
              {chiaviAudience.length === 0 ? (
                <SoftChip label="nessuna: non lo vedrebbe nessuno" tone="warning" />
              ) : (
                chiaviAudience.map((k) => <SoftChip key={k} label={k} tone="default" mono />)
              )}
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                select
                label="Aziende"
                value={modalitaTenant}
                disabled={salvataggio}
                onChange={(e) => setModalitaTenant(e.target.value as KbModalitaTenant)}
                sx={{ minWidth: 250 }}
              >
                {(Object.keys(MODALITA_TENANT_LABEL) as KbModalitaTenant[]).map((m) => (
                  <MenuItem key={m} value={m} sx={{ minHeight: TOUCH_MIN }}>
                    {MODALITA_TENANT_LABEL[m]}
                  </MenuItem>
                ))}
              </TextField>
              {modalitaTenant !== 'tutti' && (
                <Autocomplete
                  multiple
                  options={platea?.tenants ?? []}
                  disabled={salvataggio}
                  value={(platea?.tenants ?? []).filter((t) => tenantSel.includes(t.id))}
                  getOptionLabel={(t) => t.nome_azienda || t.id}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  onChange={(_e, value) => setTenantSel(value.map((t) => t.id))}
                  sx={{ flex: 1 }}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => {
                      const { key, ...tagProps } = getTagProps({ index });
                      return (
                        <Chip key={key} size="small" label={option.nome_azienda || option.id} {...tagProps} />
                      );
                    })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={modalitaTenant === 'inclusi' ? 'Aziende incluse' : 'Aziende escluse'}
                    />
                  )}
                />
              )}
            </Stack>

            <Autocomplete
              freeSolo
              options={opzioniCapability}
              disabled={salvataggio}
              value={capInformativa}
              onChange={(_e, value) => setCapInformativa(value ?? '')}
              onInputChange={(_e, value) => setCapInformativa(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Funzione citata (nota informativa)"
                  helperText={KB_HINT_CAPABILITY_INFORMATIVA}
                  InputProps={{
                    ...params.InputProps,
                    sx: { fontFamily: tokens.font.mono, fontSize: '0.85rem' },
                  }}
                />
              )}
            />

            <Autocomplete
              freeSolo
              options={opzioniCapability}
              disabled={salvataggio}
              value={capRichiesta}
              onChange={(_e, value) => setCapRichiesta(value ?? '')}
              onInputChange={(_e, value) => setCapRichiesta(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Funzione obbligatoria (nasconde il contenuto)"
                  helperText={KB_HINT_CAPABILITY_RICHIESTA}
                  InputProps={{
                    ...params.InputProps,
                    sx: { fontFamily: tokens.font.mono, fontSize: '0.85rem' },
                  }}
                />
              )}
            />

            <AnteprimaDestinatari
              contenuto={contenutoComposto}
              platea={platea}
              errorePlatea={errorePlatea}
            />
          </Sezione>

          {/* ================= ANNUNCIO ================= */}
          {formato === 'novita' && (
            <>
              <Divider />
              <Sezione
                titolo="L'annuncio"
                descrizione="Quanto pesa, per quanto resta in evidenza e dove porta."
              >
                {governance.origine === 'default' && (
                  <Alert severity="info">
                    <code>kb_settings/governance</code> non esiste ancora: durata dell&apos;evidenza
                    e canali arrivano dai valori di partenza del modello, non da una
                    configurazione salvata.
                  </Alert>
                )}

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    select
                    label="Livello di impatto"
                    value={livello}
                    disabled={salvataggio}
                    onChange={(e) => cambiaLivello(e.target.value as KbLivelloImpatto)}
                    sx={{ minWidth: 220 }}
                    error={inErrore('annuncio.livello_impatto')}
                    helperText={helper(
                      'annuncio.livello_impatto',
                      configLivello?.forza_presa_visione
                        ? 'resta in cima finché non si conferma «ho capito»'
                        : `${configLivello?.giorni_evidenza ?? 30} giorni in evidenza`,
                    )}
                  >
                    {KB_LIVELLI_IMPATTO.map((l) => (
                      <MenuItem key={l} value={l} sx={{ minHeight: TOUCH_MIN }}>
                        {KB_LIVELLO_LABEL[l]}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label="Data dell'annuncio"
                    type="date"
                    value={aValoreInputData(dataAnnuncio)}
                    disabled={salvataggio}
                    onChange={(e) => cambiaDataAnnuncio(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    helperText="una data futura tiene l'annuncio fermo fino a quel giorno"
                    sx={{ minWidth: 200 }}
                  />
                  <TextField
                    label="Fine dell'evidenza"
                    type="date"
                    value={aValoreInputData(scadenza)}
                    disabled={salvataggio}
                    onChange={(e) => {
                      setScadenzaToccata(true);
                      setScadenza(daValoreInputData(e.target.value));
                    }}
                    InputLabelProps={{ shrink: true }}
                    helperText="dopo esce dal riquadro in home, resta nell'archivio"
                    sx={{ minWidth: 200 }}
                  />
                </Stack>

                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.75 }}>
                  <Typography variant="caption" sx={{ color: tokens.color.mist }}>
                    canali dal livello scelto:
                  </Typography>
                  {canali.map((c) => (
                    <SoftChip key={c} label={c} tone={c === 'in_app' ? 'info' : 'default'} mono />
                  ))}
                  {erroriPubblicazione['annuncio.canali'] && (
                    <SoftChip label="serve almeno un canale" tone="warning" />
                  )}
                  <Typography variant="caption" sx={{ color: tokens.color.mist }}>
                    (in fase 1 l&apos;annuncio viaggia solo dentro il gestionale)
                  </Typography>
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label="Dove porta (rotta interna)"
                    value={rottaApp}
                    disabled={salvataggio}
                    onChange={(e) => setRottaApp(e.target.value)}
                    placeholder="/preventivi/nuovo"
                    error={inErrore('annuncio.rotta_app')}
                    helperText={helper('annuncio.rotta_app', KB_HINT_ROTTA_AZIONE)}
                    sx={{ flex: 1 }}
                    InputProps={{ sx: { fontFamily: tokens.font.mono, fontSize: '0.85rem' } }}
                  />
                  <TextField
                    label="Testo del bottone"
                    value={etichettaAzione}
                    disabled={salvataggio}
                    onChange={(e) => setEtichettaAzione(e.target.value)}
                    placeholder="apri un preventivo"
                    sx={{ flex: 1 }}
                  />
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label="Riferimento di rilascio"
                    value={releaseRef}
                    disabled={salvataggio}
                    onChange={(e) => setReleaseRef(e.target.value)}
                    placeholder="2026.07"
                    sx={{ width: 200 }}
                    InputProps={{ sx: { fontFamily: tokens.font.mono, fontSize: '0.85rem' } }}
                  />
                  <Autocomplete
                    multiple
                    options={opzioniCorrelati}
                    disabled={salvataggio}
                    value={correlati}
                    onChange={(_e, value) => setCorrelati(value)}
                    sx={{ flex: 1 }}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return (
                          <Chip
                            key={key}
                            size="small"
                            label={option}
                            sx={{ fontFamily: tokens.font.mono, fontSize: '0.7rem' }}
                            {...tagProps}
                          />
                        );
                      })
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Da leggere per approfondire"
                        helperText="altri contenuti collegati a questo annuncio"
                      />
                    )}
                  />
                </Stack>
              </Sezione>
            </>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={salvataggio} sx={{ minHeight: TOUCH_MIN }}>
          Annulla
        </Button>
        <Button
          variant="contained"
          startIcon={salvataggio ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
          disabled={salvataggio || bloccanti || progressoImmagine !== null}
          onClick={handleSalva}
          sx={{ minHeight: TOUCH_MIN, touchAction: 'manipulation' }}
        >
          Salva bozza
        </Button>
      </DialogActions>
    </Dialog>
  );
};
